import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useRequestDeletion, useRequestExport } from '../hooks/usePrivacy';
import { api, ApiError } from '../api/client';

type Mode = 'choose' | 'delete' | 'export';

export default function PrivacyRequestPage() {
  const [mode, setMode] = useState<Mode>('choose');

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 6 } }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography
          variant="h4"
          fontWeight={700}
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, mb: 1 }}
        >
          Your Privacy
        </Typography>
        <Typography variant="body1" color="text.secondary">
          We respect your right to control your data. Choose an option below.
        </Typography>
      </Box>

      {mode === 'choose' && <ChooseMode onSelect={setMode} />}
      {mode === 'delete' && <DeleteForm onBack={() => setMode('choose')} />}
      {mode === 'export' && <ExportForm onBack={() => setMode('choose')} />}

      <Box sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="caption" color="text.secondary">
          Powered by{' '}
          <Link href="/" color="inherit" underline="hover" fontWeight={500}>
            TimeToRSVP
          </Link>
        </Typography>
      </Box>
    </Container>
  );
}

// ─── Mode Chooser ───────────────────────────────────────────────────────────

function ChooseMode({ onSelect }: { onSelect: (mode: Mode) => void }) {
  return (
    <Stack spacing={2}>
      <Card
        sx={{
          cursor: 'pointer',
          transition: 'box-shadow 0.2s',
          '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.12)' },
        }}
        onClick={() => onSelect('delete')}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 56,
                height: 56,
                borderRadius: 3,
                bgcolor: 'error.light',
                color: 'error.contrastText',
                flexShrink: 0,
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                Delete My Data
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Request permanent deletion of all data associated with your email address.
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card
        sx={{
          cursor: 'pointer',
          transition: 'box-shadow 0.2s',
          '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.12)' },
        }}
        onClick={() => onSelect('export')}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 56,
                height: 56,
                borderRadius: 3,
                bgcolor: 'primary.light',
                color: 'primary.contrastText',
                flexShrink: 0,
              }}
            >
              <FileDownloadOutlinedIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                Export My Data
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Download a copy of all data we have associated with your email address.
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}

// ─── Delete Form ────────────────────────────────────────────────────────────

function DeleteForm({ onBack }: { onBack: () => void }) {
  const [email, setEmail] = useState('');
  const [requestId, setRequestId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const deletionMutation = useRequestDeletion();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const result = await deletionMutation.mutateAsync(email.trim());
      setRequestId(result.id);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Something went wrong. Please try again.');
      }
    }
  };

  if (requestId) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 5, px: 3 }}>
          <CheckCircleOutlineIcon color="success" sx={{ fontSize: 56, mb: 2 }} />
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Request Submitted
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Your data deletion request has been received. We will process it shortly.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Reference ID: <strong>{requestId}</strong>
          </Typography>
          <Button variant="outlined" onClick={onBack}>
            Back
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          size="small"
          sx={{ mb: 2 }}
        >
          Back
        </Button>

        <Typography variant="h6" fontWeight={600} gutterBottom>
          Delete My Data
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Enter the email address associated with your guest records. All your
          personal data will be permanently anonymised and cannot be recovered.
        </Typography>

        <form onSubmit={handleSubmit}>
          <Stack spacing={2.5}>
            <TextField
              label="Email address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              autoFocus
              placeholder="you@example.com"
            />

            {error && <Alert severity="error">{error}</Alert>}

            <Button
              type="submit"
              variant="contained"
              color="error"
              size="large"
              fullWidth
              disabled={!email.trim() || deletionMutation.isPending}
              startIcon={
                deletionMutation.isPending ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <DeleteOutlineIcon />
                )
              }
              sx={{ py: 1.5 }}
            >
              {deletionMutation.isPending ? 'Submitting...' : 'Request Data Deletion'}
            </Button>
          </Stack>
        </form>
      </CardContent>
    </Card>
  );
}

// ─── Export Form ────────────────────────────────────────────────────────────

function ExportForm({ onBack }: { onBack: () => void }) {
  const [email, setEmail] = useState('');
  const [exportId, setExportId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const exportMutation = useRequestExport();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      const result = await exportMutation.mutateAsync(email.trim());
      setExportId(result.id);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Something went wrong. Please try again.');
      }
    }
  };

  if (exportId) {
    const downloadUrl = api.privacy.downloadExport(exportId);

    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 5, px: 3 }}>
          <CheckCircleOutlineIcon color="success" sx={{ fontSize: 56, mb: 2 }} />
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Export Ready
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your data export has been generated. Click below to download it.
          </Typography>
          <Stack spacing={2} alignItems="center">
            <Button
              variant="contained"
              size="large"
              startIcon={<FileDownloadOutlinedIcon />}
              href={downloadUrl}
              target="_blank"
              sx={{ py: 1.5 }}
            >
              Download My Data
            </Button>
            <Button variant="outlined" onClick={onBack}>
              Back
            </Button>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          size="small"
          sx={{ mb: 2 }}
        >
          Back
        </Button>

        <Typography variant="h6" fontWeight={600} gutterBottom>
          Export My Data
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Enter the email address associated with your guest records. We will
          generate a JSON file containing all data we hold about you.
        </Typography>

        <form onSubmit={handleSubmit}>
          <Stack spacing={2.5}>
            <TextField
              label="Email address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              autoFocus
              placeholder="you@example.com"
            />

            {error && <Alert severity="error">{error}</Alert>}

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={!email.trim() || exportMutation.isPending}
              startIcon={
                exportMutation.isPending ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <FileDownloadOutlinedIcon />
                )
              }
              sx={{ py: 1.5 }}
            >
              {exportMutation.isPending ? 'Generating...' : 'Export My Data'}
            </Button>
          </Stack>
        </form>
      </CardContent>
    </Card>
  );
}
